import type { MemoItem } from '@/shared/templates/memos/lib/renderMemo'

/**
 * A run of candidates sharing one newsvalue. A group without a `newsvalue` is
 * the one holding what carries none - the print articles of a Print-PM, or the
 * odd item the index returned without one - and is rendered without a heading.
 */
export interface MemoNewsvalueGroup {
  newsvalue?: string
  items: MemoItem[]
}

function rank(newsvalue: string): number {
  const value = Number(newsvalue)

  // An unrecognizable newsvalue is still a newsvalue, so it keeps its own
  // heading - it just sorts after the ones that can be compared.
  if (Number.isNaN(value)) {
    return -Infinity
  }

  return value
}

/**
 * Group candidates by newsvalue, highest first, keeping the order they came in
 * within each group.
 *
 * A list where nothing carries a newsvalue comes back as a single group with
 * none, which is how a column of print articles keeps rendering as the plain
 * list it always was.
 */
export function groupByNewsvalue(items: MemoItem[]): MemoNewsvalueGroup[] {
  const byNewsvalue = new Map<string, MemoItem[]>()
  const without: MemoItem[] = []

  for (const item of items) {
    if (!item.newsvalue) {
      without.push(item)
      continue
    }

    const group = byNewsvalue.get(item.newsvalue)

    if (group) {
      group.push(item)
    } else {
      byNewsvalue.set(item.newsvalue, [item])
    }
  }

  if (!byNewsvalue.size) {
    return [{ items }]
  }

  const groups = [...byNewsvalue.entries()]
    .sort(([a], [b]) => rank(b) - rank(a))
    .map(([newsvalue, groupItems]): MemoNewsvalueGroup => ({ newsvalue, items: groupItems }))

  if (without.length) {
    groups.push({ items: without })
  }

  return groups
}
